/**
 * Printer Settings Component
 * Allows users to configure their default printers for printing operations
 * Uses abstracted printer service for easy replacement
 * - Set default ZPL printer (for thermal labels)
 * - Set default PDF printer (for documents)
 * - Test print functionality
 * - View printer service connection status
 */

import React from "react";
import ReactDOM from "react-dom";
import * as GlobalService from "../../global_services";
import "./printer-settings.scss";

// ============================================================================
// TEST LABEL CONSTANTS
// ============================================================================

/**
 * Professional ZPL test label for 4x6 inch thermal printers (203 DPI standard)
 * Includes: Header, barcode, simulated address, and validation instructions
 */
const TEST_LABEL_ZPL_4X6 = `^XA
^FO50,50^A0N,50,50^FDTEST LABEL^FS
^FO50,120^A0N,30,30^FD4 x 6 inches^FS

^FO50,180^BY3^BCN,100,Y,N,N^FDTEST123456^FS

^FO50,320^A0N,25,25^FDTEST SHIPPING ADDRESS^FS
^FO50,360^A0N,20,20^FD123 Main Street^FS
^FO50,395^A0N,20,20^FDAnywhere, ST 12345^FS
^FO50,430^A0N,20,20^FDUnited States^FS

^FO50,500^GB750,3,3^FS

^FO50,520^A0N,18,18^FDIf this label measures 4x6 inches^FS
^FO50,550^A0N,18,18^FDand is clearly readable, your^FS
^FO50,580^A0N,18,18^FDprinter is configured correctly!^FS

^FO50,1150^A0N,15,15^FDTest Label - Printer Settings^FS
^XZ`;

/** Log prefix for all console output in this component */
const LOG_PREFIX = "[Printer Settings]";

/** Shared messages for test prints */
const MSG_TEST_PRINT_SUCCESS = "Test page sent to printer successfully!";
const MSG_TEST_PRINT_FAILED = "Test print failed";

/** Message prefix when a test print fails */
const TEST_PRINT_FAILED_PREFIX = MSG_TEST_PRINT_FAILED + ": ";

/** Console log prefix when a test print fails */
const LOG_TEST_PRINT_FAILED = LOG_PREFIX + " ✗ " + MSG_TEST_PRINT_FAILED + ":";

/** Error message when printer service failed to load */
const MSG_PRINTER_SERVICE_NOT_LOADED = "Printer service not loaded. Please refresh the page.";

/**
 * Generate a proper 4x6 inch PDF test label (288pt x 432pt at 72 DPI)
 * Returns base64-encoded PDF string
 */
function generateTestPdf4x6() {
    // PDF with exact 4x6 MediaBox (288pt x 432pt)
    // Includes header, barcode placeholder, address block, and validation text
    const pdfContent = `%PDF-1.4
1 0 obj<</Type/Catalog/Pages 2 0 R>>endobj
2 0 obj<</Type/Pages/Count 1/Kids[3 0 R]>>endobj
3 0 obj<</Type/Page/Parent 2 0 R/MediaBox[0 0 288 432]/Contents 4 0 R/Resources<</Font<</F1 5 0 R/F2 6 0 R>>>>>>endobj
4 0 obj<</Length 580>>stream
BT
/F1 24 Tf
20 400 Td
(TEST LABEL) Tj
/F2 14 Tf
0 -25 Td
(4 x 6 inches) Tj

0 -50 Td
/F1 12 Tf
(Barcode: TEST123456) Tj

0 -40 Td
/F2 16 Tf
(TEST SHIPPING ADDRESS) Tj
0 -22 Td
/F1 14 Tf
(123 Main Street) Tj
0 -20 Td
(Anywhere, ST 12345) Tj
0 -20 Td
(United States) Tj

0 -50 Td
/F2 12 Tf
(If this label measures 4x6 inches) Tj
0 -18 Td
(and is clearly readable, your) Tj
0 -18 Td
(printer is configured correctly!) Tj

0 -80 Td
/F1 10 Tf
(Test Label - Printer Settings) Tj
ET
endstream endobj
5 0 obj<</Type/Font/Subtype/Type1/BaseFont/Helvetica>>endobj
6 0 obj<</Type/Font/Subtype/Type1/BaseFont/Helvetica-Bold>>endobj
xref
0 7
0000000000 65535 f
0000000009 00000 n
0000000056 00000 n
0000000115 00000 n
0000000244 00000 n
0000000874 00000 n
0000000943 00000 n
trailer<</Size 7/Root 1 0 R>>
startxref
1017
%%EOF`;

    return btoa(pdfContent);
}

export class PrinterSettingsModal extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: false,
            loadingMessage: "",
            printerService: null,

            // Connection status
            isConnected: false,
            isConnecting: false,
            connectionError: null,

            // Printers
            availablePrinters: [],
            printersWithDetails: [], // New: printers with capability info
            loadingPrinters: false,

            // Current selections
            selectedZplPrinter: "",
            selectedPdfPrinter: "",
            selectedLabelSize: "4x6", // Default label size for PDF printing
            selectedFullPagePrinter: "", // NEW: Full-page document printer (8.5x11)

            // PDF printing mode
            silentPdfPrint: false, // If true, print directly; if false, show browser dialog
            silentFullPagePrint: false, // NEW: Silent print for full-page documents

            // UI state
            showSettingsWhenDisconnected: false, // Allow editing settings even when QZ Tray isn't connected
        };
    }

    componentDidMount() {
        this._isMounted = true;
        this.loadPrinterService();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    /** Guarded setState to avoid updates after unmount (fixes memory leak warning) */
    _safeSetState = (state) => {
        if (this._isMounted) {
            this.setState(state);
        }
    };

    /**
     * Load printer service and initialize (uses abstraction layer)
     */
    loadPrinterService = async () => {
        this._safeSetState({
            loading: true,
            loadingMessage: "Loading printing service...",
        });

        try {
            const printerService = await import("../../services/printer.service.js");
            this._safeSetState({ printerService });

            // Saved preferences live in localStorage, so load them even if QZ is offline.
            this.loadCurrentSettings(printerService);

            // Connect to printer service
            await this.connectToPrinterService(printerService);
        } catch (error) {
            console.error(LOG_PREFIX + " Failed to load printer service:", error);
            this._safeSetState({
                loading: false,
                connectionError: "Failed to load printing service. Please refresh the page.",
            });
        }
    };

    /**
     * Connect to printer service
     * Uses Promise deduplication - if a connection is already in progress,
     * this will wait for that connection rather than starting a new one.
     */
    connectToPrinterService = async (printerService, forceReconnect = false) => {
        if (!printerService) {
            this._safeSetState({ connectionError: MSG_PRINTER_SERVICE_NOT_LOADED });
            return;
        }
        this._safeSetState({
            isConnecting: true,
            loadingMessage: "Connecting to printer service...",
        });

        try {
            const cachedConnectionStatus = GlobalService.getQzTrayConnected();

            if (cachedConnectionStatus && !forceReconnect) {
                const actuallyConnected = printerService.isConnected();

                if (actuallyConnected) {
                    this._safeSetState({
                        isConnected: true,
                        isConnecting: false,
                        connectionError: null,
                        loading: false,
                    });
                    // Still load printers so saved selections can appear in dropdowns
                    await this.loadPrinters(printerService);
                    return;
                }
            }

            if (forceReconnect) {
                try {
                    await printerService.disconnectFromService(true);
                } catch (e) {
                    // Ignore disconnect errors
                }

                await printerService.connectToService();

                const actuallyConnected = printerService.isConnected();

                if (actuallyConnected) {
                    console.log(LOG_PREFIX + " Connected to QZ Tray");
                    this._safeSetState({
                        isConnected: true,
                        isConnecting: false,
                        connectionError: null,
                        loading: false,
                    });
                    // Update global state (service no longer does this to avoid circular dependency)
                    GlobalService.setQzTrayConnected(true);

                    // Load printers now that we're connected
                    await this.loadPrinters(printerService);
                    return;
                } else {
                    throw new Error("Connection succeeded but QZ Tray is not responding");
                }
            }

            await printerService.connectToService();

            const actuallyConnected = printerService.isConnected();

            if (actuallyConnected) {
                console.log(LOG_PREFIX + " Connected to QZ Tray");
                this._safeSetState({
                    isConnected: true,
                    isConnecting: false,
                    connectionError: null,
                    loading: false,
                });
                // Update global state (service no longer does this to avoid circular dependency)
                GlobalService.setQzTrayConnected(true);

                // Load printers now that we're connected
                await this.loadPrinters(printerService);
                return;
            }

            this._safeSetState({
                isConnected: false,
                isConnecting: false,
                loading: false,
                connectionError:
                    "QZ Tray is not running or not installed. Please download and install QZ Tray, then restart this application.",
            });
        } catch (error) {
            console.error(LOG_PREFIX + " Failed to connect:", error?.message || error);
            this._safeSetState({
                isConnected: false,
                isConnecting: false,
                loading: false,
                connectionError:
                    "QZ Tray is not running or not installed. Please download and install QZ Tray, then restart this application.",
            });
            // Update global state (service no longer does this to avoid circular dependency)
            GlobalService.setQzTrayConnected(false);
        }
    };

    /**
     * Load current printer settings from localStorage
     */
    loadCurrentSettings = (printerService) => {
        const zplPrinter = printerService.getDefaultZplPrinter();
        const pdfPrinter = printerService.getDefaultPdfPrinter();

        const silentPrint = localStorage.getItem("qz_silent_pdf_print") === "true";
        const labelSize = localStorage.getItem("qz_default_label_size") || "4x6";

        const fullPagePrinter = localStorage.getItem("qz_default_fullpage_printer") || "";
        const silentFullPage = localStorage.getItem("qz_silent_fullpage_print") === "true";

        this._safeSetState({
            selectedZplPrinter: zplPrinter || "",
            selectedPdfPrinter: pdfPrinter || "",
            silentPdfPrint: silentPrint,
            selectedLabelSize: labelSize,
            selectedFullPagePrinter: fullPagePrinter,
            silentFullPagePrint: silentFullPage,
        });
    };

    /**
     * Load available printers from printer service
     */
    loadPrinters = async (printerService) => {
        this._safeSetState({ loadingPrinters: true });

        try {
            const printersWithDetails = ((await printerService.findPrinterSummaries()) || []).slice().sort((a, b) =>
                a.name.localeCompare(b.name, undefined, {
                    sensitivity: "base",
                    numeric: true,
                })
            );

            const printerNames = printersWithDetails.map((p) => p.name);

            this._safeSetState({
                availablePrinters: printerNames,
                printersWithDetails: printersWithDetails,
                loadingPrinters: false,
            });
        } catch (error) {
            console.error(LOG_PREFIX + " Failed to load printers:", error);
            this._safeSetState({
                availablePrinters: [],
                printersWithDetails: [],
                loadingPrinters: false,
            });
        }
    };

    /**
     * Handle ZPL printer selection change
     */
    onZplPrinterChange = (e) => {
        this.setState({ selectedZplPrinter: e.target.value });
    };

    onPdfPrinterChange = (e) => {
        this.setState({ selectedPdfPrinter: e.target.value });
    };

    /**
     * Handle label size selection change
     */
    onLabelSizeChange = (e) => {
        const size = e.target.value;
        this.setState({ selectedLabelSize: size });
    };

    /**
     * Handle full-page printer selection change
     */
    onFullPagePrinterChange = (e) => {
        const printer = e.target.value;
        this.setState({ selectedFullPagePrinter: printer });
    };

    /**
     * Handle silent full-page print checkbox change
     */
    onSilentFullPagePrintChange = (e) => {
        const checked = e.target.checked;
        this.setState({ silentFullPagePrint: checked });
    };

    /**
     * Render printer option element (helper to avoid code duplication)
     */
    renderPrinterOption = (printer) => (
        <option key={printer.name} value={printer.name}>
            {printer.name}
        </option>
    );

    /**
     * Render fallback printer options when printersWithDetails is empty (shared for PDF and full-page dropdowns)
     */
    renderFallbackPrinterOptions = (availablePrinters) =>
        availablePrinters.map((printer) => (
            <option key={printer} value={printer}>
                {printer}
            </option>
        ));

    /**
     * Save printer settings
     */
    onSave = () => {
        const {
            printerService,
            selectedZplPrinter,
            selectedPdfPrinter,
            silentPdfPrint,
            selectedLabelSize,
            selectedFullPagePrinter,
            silentFullPagePrint,
            isConnected,
        } = this.state;

        // Only save printer selections if connected (printerService available)
        if (isConnected && printerService) {
            // Save or clear ZPL printer
            if (selectedZplPrinter && selectedZplPrinter !== "") {
                printerService.setDefaultZplPrinter(selectedZplPrinter);
            } else {
                printerService.clearDefaultZplPrinter();
            }

            if (selectedPdfPrinter && selectedPdfPrinter !== "") {
                printerService.setDefaultPdfPrinter(selectedPdfPrinter);
            } else {
                printerService.clearDefaultPdfPrinter();
            }
        }

        localStorage.setItem("qz_silent_pdf_print", silentPdfPrint.toString());
        localStorage.setItem("qz_default_label_size", selectedLabelSize);
        localStorage.setItem("qz_default_fullpage_printer", selectedFullPagePrinter);
        localStorage.setItem("qz_silent_fullpage_print", silentFullPagePrint.toString());

        console.log(LOG_PREFIX + " Settings saved successfully");

        // Notify user
        if (window.notification) {
            window.notification("success", "Printer settings saved successfully");
        }

        // Close modal
        if (this.props.onClose) {
            this.props.onClose();
        }
    };

    /**
     * Test ZPL printer
     */
    onTestZplPrinter = async () => {
        const { printerService, selectedZplPrinter } = this.state;

        if (!printerService) {
            GlobalService.notification("error", MSG_PRINTER_SERVICE_NOT_LOADED);
            return;
        }

        if (!selectedZplPrinter) {
            GlobalService.notification("warning", "Please select a ZPL printer first");
            return;
        }

        this._safeSetState({ loading: true, loadingMessage: "Printing test label..." });

        try {
            // Use professional 4x6 test label with barcode and realistic content
            await printerService.printZpl(TEST_LABEL_ZPL_4X6, selectedZplPrinter);
            console.log(LOG_PREFIX + " Test label printed successfully");
            this._safeSetState({ loading: false });
            GlobalService.notification("success", MSG_TEST_PRINT_SUCCESS);
        } catch (error) {
            console.error(LOG_TEST_PRINT_FAILED, error);
            this._safeSetState({ loading: false });
            GlobalService.notification("error", TEST_PRINT_FAILED_PREFIX + error.message);
        }
    };

    /**
     * Test PDF printer
     */
    onTestPdfPrinter = async () => {
        const { printerService, selectedPdfPrinter, selectedLabelSize } = this.state;

        if (!printerService) {
            GlobalService.notification("error", MSG_PRINTER_SERVICE_NOT_LOADED);
            return;
        }

        if (!selectedPdfPrinter) {
            GlobalService.notification("warning", "Please select a PDF printer first");
            return;
        }

        this._safeSetState({ loading: true, loadingMessage: "Printing test page..." });

        try {
            // Generate proper 4x6 inch test label PDF (288pt x 432pt MediaBox)
            const testPdfBase64 = generateTestPdf4x6();

            // Pass the selected label size to ensure correct scaling
            await printerService.printPdf(testPdfBase64, selectedPdfPrinter, {
                labelSize: selectedLabelSize,
            });

            console.log(LOG_PREFIX + " Test page printed successfully");
            this._safeSetState({ loading: false });
            GlobalService.notification("success", MSG_TEST_PRINT_SUCCESS);
        } catch (error) {
            console.error(LOG_TEST_PRINT_FAILED, error);
            this._safeSetState({ loading: false });
            GlobalService.notification("error", TEST_PRINT_FAILED_PREFIX + error.message);
        }
    };

    /**
     * Test full-page printer
     */
    onTestFullPagePrinter = async () => {
        const { printerService, selectedFullPagePrinter } = this.state;

        if (!printerService) {
            GlobalService.notification("error", MSG_PRINTER_SERVICE_NOT_LOADED);
            return;
        }

        if (!selectedFullPagePrinter) {
            GlobalService.notification("warning", "Please select a full-page printer first");
            return;
        }

        this._safeSetState({ loading: true, loadingMessage: "Printing test page..." });

        try {
            // Generate 8.5x11 inch test PDF
            const testPdfBase64 = this.generateTestFullPagePdf();

            // No labelSize specified = uses PDF's natural size (8.5x11)
            await printerService.printPdf(testPdfBase64, selectedFullPagePrinter);

            console.log(LOG_PREFIX + " Test page printed successfully");
            this._safeSetState({ loading: false });
            GlobalService.notification("success", MSG_TEST_PRINT_SUCCESS);
        } catch (error) {
            console.error(LOG_TEST_PRINT_FAILED, error);
            this._safeSetState({ loading: false });
            GlobalService.notification("error", TEST_PRINT_FAILED_PREFIX + error.message);
        }
    };

    /**
     * Generate 8.5x11 inch test PDF for full-page printer (realistic packing slip example)
     */
    generateTestFullPagePdf = () => {
        // Realistic packing slip example (Letter size: 612pt x 792pt)
        const pdfContent = `%PDF-1.4
1 0 obj<</Type/Catalog/Pages 2 0 R>>endobj
2 0 obj<</Type/Pages/Count 1/Kids[3 0 R]>>endobj
3 0 obj<</Type/Page/Parent 2 0 R/MediaBox[0 0 612 792]/Contents 4 0 R/Resources<</Font<</F1<</Type/Font/Subtype/Type1/BaseFont/Helvetica-Bold>>/F2<</Type/Font/Subtype/Type1/BaseFont/Helvetica>>>>>>endobj
4 0 obj<</Length 1680>>stream
BT
/F1 18 Tf
50 740 Td
(TEST PACKING SLIP) Tj
/F2 10 Tf
450 0 Td
(Date: 02/09/2026) Tj
-450 -30 Td
(Order #: TEST-12345) Tj
0 -15 Td
(Ship To:) Tj
/F1 11 Tf
0 -20 Td
(John Smith) Tj
/F2 10 Tf
0 -15 Td
(123 Main Street) Tj
0 -15 Td
(Apt 4B) Tj
0 -15 Td
(New York, NY 10001) Tj
0 -15 Td
(United States) Tj
0 -30 Td
/F1 11 Tf
(Ship From:) Tj
/F2 10 Tf
0 -20 Td
(Your Warehouse) Tj
0 -15 Td
(456 Industrial Pkwy) Tj
0 -15 Td
(Chicago, IL 60601) Tj
0 -40 Td
/F1 11 Tf
(Shipping Method: Ground - 3-5 Business Days) Tj
0 -15 Td
(Tracking #: 1Z999AA10123456784) Tj
0 -35 Td
/F1 12 Tf
(ITEMS IN THIS SHIPMENT) Tj
/F2 10 Tf
0 -25 Td
(-------------------------------------------------------------------) Tj
0 -18 Td
/F1 10 Tf
(SKU) Tj
150 0 Td
(Product) Tj
200 0 Td
(Qty) Tj
-350 -18 Td
/F2 10 Tf
(-------------------------------------------------------------------) Tj
0 -18 Td
(WIDGET-001) Tj
150 0 Td
(Blue Widget - Size M) Tj
200 0 Td
(2) Tj
-350 -18 Td
(GADGET-202) Tj
150 0 Td
(Red Gadget Premium) Tj
200 0 Td
(1) Tj
-350 -18 Td
(TOOL-555) Tj
150 0 Td
(Universal Tool Kit) Tj
200 0 Td
(1) Tj
-350 -18 Td
(-------------------------------------------------------------------) Tj
0 -25 Td
/F1 10 Tf
(Total Items: 4) Tj
0 -25 Td
/F2 9 Tf
(Special Instructions: Handle with care - Fragile items) Tj
0 -50 Td
(-------------------------------------------------------------------) Tj
0 -18 Td
/F1 10 Tf
(This is a TEST packing slip from Printer Settings) Tj
/F2 9 Tf
0 -15 Td
(If this document prints clearly on full-page paper \\(8.5 x 11 in.\\),) Tj
0 -12 Td
(your full-page printer is configured correctly!) Tj
ET
endstream endobj
xref
0 5
0000000000 65535 f
0000000009 00000 n
0000000056 00000 n
0000000115 00000 n
0000000295 00000 n
trailer<</Size 5/Root 1 0 R>>
startxref
2024
%%EOF`;

        // Convert to base64
        return btoa(pdfContent);
    };

    /**
     * Refresh connection to printer service
     */
    onRefreshConnection = () => {
        if (!this.state.printerService) {
            this.setState({ connectionError: MSG_PRINTER_SERVICE_NOT_LOADED });
            return;
        }
        console.log(LOG_PREFIX + " Refreshing connection (forcing real connection attempt)...");
        this.setState({
            connectionError: null,
            testPrintStatus: null,
        });
        this.connectToPrinterService(this.state.printerService, true);
    };

    /**
     * Refresh printer list
     */
    onRefreshPrinters = () => {
        if (!this.state.printerService) {
            this.setState({ connectionError: MSG_PRINTER_SERVICE_NOT_LOADED });
            return;
        }
        console.log(LOG_PREFIX + " Refreshing printer list...");
        this.loadPrinters(this.state.printerService);
    };

    render() {
        const {
            loading,
            loadingMessage,
            isConnected,
            connectionError,
            availablePrinters,
            loadingPrinters,
            selectedZplPrinter,
            selectedPdfPrinter,
        } = this.state;

        // Use React Portal to render modal outside the Pack & Ship modal DOM tree
        // This prevents DOM nesting conflicts and z-index issues
        const modalContent = (
            <div className="printer-settings-modal-overlay">
                <div className="printer-settings-modal">
                    <div className="printer-settings-header">
                        <div className="header-left">
                            <h2>Printer Settings</h2>
                            {/* Connection Status - Compact */}
                            {connectionError ? (
                                <span className="connection-badge disconnected">
                                    <i className="fa fa-exclamation-circle"></i> QZ Tray Not Connected
                                </span>
                            ) : isConnected ? (
                                <span className="connection-badge connected">
                                    <i className="fa fa-check-circle"></i> Connected
                                </span>
                            ) : (
                                <span className="connection-badge connecting">
                                    <i className="fa fa-spinner fa-spin"></i> Connecting...
                                </span>
                            )}
                        </div>
                        <div className="header-right">
                            {(isConnected || this.state.showSettingsWhenDisconnected) && (
                                <button
                                    onClick={this.onRefreshPrinters}
                                    className="btn-refresh-compact"
                                    title="Refresh printer list"
                                >
                                    <i className="fa fa-refresh"></i> Refresh
                                </button>
                            )}
                            <button className="close-btn" onClick={this.props.onClose} aria-label="Close">
                                ×
                            </button>
                        </div>
                    </div>

                    <div className="printer-settings-body">
                        {loading && (
                            <div className="loading-overlay">
                                <i className="fa fa-spinner fa-spin"></i>
                                <p>{loadingMessage}</p>
                            </div>
                        )}

                        {/* Show setup steps only when disconnected */}
                        {connectionError && (
                            <div className="setup-guide">
                                <h4>QZ Tray Not Detected</h4>
                                <p className="status-description">Follow these simple steps to enable printing</p>

                                {/* Step-by-Step Guide */}
                                <div className="setup-steps">
                                    {/* Step 1: Download */}
                                    <div className="setup-step">
                                        <div className="step-indicator">
                                            <span className="step-number">1</span>
                                        </div>
                                        <div className="step-content">
                                            <h5>Download QZ Tray</h5>
                                            <p>Get the free printing software</p>
                                            <a
                                                href="https://qz.io/download/"
                                                target="_blank"
                                                className="btn-step-action"
                                                rel="noreferrer"
                                            >
                                                <i className="fa fa-download"></i>
                                                Download Now
                                            </a>
                                        </div>
                                    </div>

                                    {/* Step 2: Install */}
                                    <div className="setup-step">
                                        <div className="step-indicator">
                                            <span className="step-number">2</span>
                                        </div>
                                        <div className="step-content">
                                            <h5>Install on Your Computer & Launch QZ Tray</h5>
                                            <p>Run the downloaded file and follow the prompts</p>
                                            <div className="step-hint">
                                                <i className="fa fa-lightbulb-o"></i>
                                                Takes less than 2 minutes
                                            </div>
                                        </div>
                                    </div>

                                    {/* Step 3: Connect */}
                                    <div className="setup-step">
                                        <div className="step-indicator">
                                            <span className="step-number">3</span>
                                        </div>
                                        <div className="step-content">
                                            <h5>Connect to Extensiv</h5>
                                            <p>Launch QZ Tray and click the button below</p>
                                            <button onClick={this.onRefreshConnection} className="btn-step-action">
                                                <i className="fa fa-rocket"></i>
                                                Connect Now
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Small edit settings link at bottom */}
                                <button
                                    onClick={() => this.setState({ showSettingsWhenDisconnected: true })}
                                    className="btn-edit-settings-small"
                                >
                                    <i className="fa fa-cog"></i>
                                    Edit settings without connecting
                                </button>
                            </div>
                        )}

                        {(isConnected || this.state.showSettingsWhenDisconnected) && (
                            <>
                                {/* Card-Based Printer Configuration - Document-First Approach */}
                                <div className="printer-cards-container">
                                    {/* Card 1: Shipping Labels */}
                                    <div className="printer-card">
                                        <div className="card-header">
                                            <div className="card-icon">📦</div>
                                            <div className="card-title">
                                                <h3>Shipping Labels</h3>
                                                <p className="card-subtitle">Small labels (4x6 inches)</p>
                                            </div>
                                        </div>

                                        <div className="card-body">
                                            {loadingPrinters ? (
                                                <div className="loading-printers-inline">
                                                    <i className="fa fa-spinner fa-spin"></i> Loading printers...
                                                </div>
                                            ) : (
                                                <>
                                                    {/* ZPL Printer */}
                                                    <div className="printer-type-section">
                                                        <label className="printer-label">
                                                            ZPL Labels (Zebra printers):
                                                        </label>

                                                        <div className="printer-row">
                                                            <select
                                                                value={selectedZplPrinter}
                                                                onChange={this.onZplPrinterChange}
                                                                className="printer-dropdown-card"
                                                            >
                                                                <option value="">-- Select Printer --</option>
                                                                {/* Thermal/ZPL printers first (recommended) */}
                                                                {this.state.printersWithDetails.filter(
                                                                    (p) => p.isZebraThermal || p.supportsZpl
                                                                ).length > 0 && (
                                                                    <optgroup label="🏷️ ZPL Printers (Recommended)">
                                                                        {this.state.printersWithDetails
                                                                            .filter(
                                                                                (p) => p.isZebraThermal || p.supportsZpl
                                                                            )
                                                                            .map(this.renderPrinterOption)}
                                                                    </optgroup>
                                                                )}
                                                                {/* Other printers */}
                                                                {this.state.printersWithDetails.filter(
                                                                    (p) => !(p.isZebraThermal || p.supportsZpl)
                                                                ).length > 0 && (
                                                                    <optgroup label="🖨️ Other Printers">
                                                                        {this.state.printersWithDetails
                                                                            .filter(
                                                                                (p) =>
                                                                                    !(p.isZebraThermal || p.supportsZpl)
                                                                            )
                                                                            .map(this.renderPrinterOption)}
                                                                    </optgroup>
                                                                )}
                                                                {/* Fallback */}
                                                                {this.state.printersWithDetails.length === 0 &&
                                                                    this.renderFallbackPrinterOptions(
                                                                        availablePrinters
                                                                    )}
                                                            </select>

                                                            <button
                                                                onClick={this.onTestZplPrinter}
                                                                className="btn-test-card"
                                                                disabled={!selectedZplPrinter}
                                                            >
                                                                <i className="fa fa-print"></i> Test
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* PDF Printer */}
                                                    <div className="printer-type-section">
                                                        <label className="printer-label">
                                                            PDF Labels (all printers):
                                                        </label>

                                                        <div className="printer-row">
                                                            <select
                                                                value={selectedPdfPrinter}
                                                                onChange={this.onPdfPrinterChange}
                                                                className="printer-dropdown-card"
                                                            >
                                                                <option value="">-- Select Printer --</option>
                                                                {/* Thermal/Label printers first (recommended) */}
                                                                {this.state.printersWithDetails.filter(
                                                                    (p) => p.isThermal
                                                                ).length > 0 && (
                                                                    <optgroup label="🏷️ Label Printers (Recommended)">
                                                                        {this.state.printersWithDetails
                                                                            .filter((p) => p.isThermal)
                                                                            .map(this.renderPrinterOption)}
                                                                    </optgroup>
                                                                )}
                                                                {/* Office printers */}
                                                                {this.state.printersWithDetails.filter(
                                                                    (p) => !p.isThermal
                                                                ).length > 0 && (
                                                                    <optgroup label="🖨️ Office Printers">
                                                                        {this.state.printersWithDetails
                                                                            .filter((p) => !p.isThermal)
                                                                            .map(this.renderPrinterOption)}
                                                                    </optgroup>
                                                                )}
                                                                {/* Fallback */}
                                                                {this.state.printersWithDetails.length === 0 &&
                                                                    this.renderFallbackPrinterOptions(
                                                                        availablePrinters
                                                                    )}
                                                            </select>

                                                            <button
                                                                onClick={this.onTestPdfPrinter}
                                                                className="btn-test-card"
                                                                disabled={!selectedPdfPrinter}
                                                            >
                                                                <i className="fa fa-print"></i> Test
                                                            </button>
                                                        </div>

                                                        <div className="card-options">
                                                            <label className="checkbox-inline">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={this.state.silentPdfPrint}
                                                                    onChange={(e) =>
                                                                        this.setState({
                                                                            silentPdfPrint: e.target.checked,
                                                                        })
                                                                    }
                                                                />
                                                                <span>Skip PDF preview (print immediately)</span>
                                                            </label>

                                                            {/* Temporarily hidden - keeping PDF size at default 4x6 */}
                                                            {/* {this.state.silentPdfPrint && (
                                                                <div className="label-size-compact">
                                                                    <label>Label size:</label>
                                                                    <select
                                                                        value={this.state.selectedLabelSize}
                                                                        onChange={this.onLabelSizeChange}
                                                                        className="size-dropdown-compact"
                                                                    >
                                                                        <option value="4x6">4x6 in.</option>
                                                                        <option value="4x8">4x8 in.</option>
                                                                        <option value="4x4">4x4 in.</option>
                                                                        <option value="6x4">6x4 in.</option>
                                                                    </select>
                                                                </div>
                                                            )} */}
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Card 2: Packing Slips */}
                                    <div className="printer-card">
                                        <div className="card-header">
                                            <div className="card-icon">📄</div>
                                            <div className="card-title">
                                                <h3>Documents</h3>
                                                <p className="card-subtitle">Full page (8.5 x 11 inches)</p>
                                            </div>
                                        </div>

                                        <div className="card-body">
                                            {loadingPrinters ? (
                                                <div className="loading-printers-inline">
                                                    <i className="fa fa-spinner fa-spin"></i> Loading printers...
                                                </div>
                                            ) : (
                                                <>
                                                    <label className="printer-label">Where should these print?</label>

                                                    <div className="printer-row">
                                                        <select
                                                            value={this.state.selectedFullPagePrinter}
                                                            onChange={this.onFullPagePrinterChange}
                                                            className="printer-dropdown-card"
                                                        >
                                                            <option value="">-- Select Printer --</option>
                                                            {/* Office printers first (recommended) */}
                                                            {this.state.printersWithDetails.filter((p) => !p.isThermal)
                                                                .length > 0 && (
                                                                <optgroup label="🖨️ Office Printers (Recommended)">
                                                                    {this.state.printersWithDetails
                                                                        .filter((p) => !p.isThermal)
                                                                        .map(this.renderPrinterOption)}
                                                                </optgroup>
                                                            )}
                                                            {/* Thermal printers with warning */}
                                                            {this.state.printersWithDetails.filter((p) => p.isThermal)
                                                                .length > 0 && (
                                                                <optgroup label="⚠️ Label Printers (Not Recommended)">
                                                                    {this.state.printersWithDetails
                                                                        .filter((p) => p.isThermal)
                                                                        .map(this.renderPrinterOption)}
                                                                </optgroup>
                                                            )}
                                                            {/* Fallback */}
                                                            {this.state.printersWithDetails.length === 0 &&
                                                                this.renderFallbackPrinterOptions(availablePrinters)}
                                                        </select>

                                                        <button
                                                            onClick={this.onTestFullPagePrinter}
                                                            className="btn-test-card"
                                                            disabled={!this.state.selectedFullPagePrinter}
                                                        >
                                                            <i className="fa fa-print"></i> Test
                                                        </button>
                                                    </div>

                                                    <div className="card-options">
                                                        <label className="checkbox-inline">
                                                            <input
                                                                type="checkbox"
                                                                checked={this.state.silentFullPagePrint}
                                                                onChange={this.onSilentFullPagePrintChange}
                                                            />
                                                            <span>Skip PDF preview (print immediately)</span>
                                                        </label>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="printer-settings-footer">
                        <button onClick={this.props.onClose} className="btn-secondary">
                            Cancel
                        </button>
                        <button onClick={this.onSave} className="btn-primary">
                            Save Settings
                        </button>
                    </div>
                </div>
            </div>
        );

        // Render modal content to document.body using React Portal
        // This avoids DOM hierarchy conflicts with nested modals
        return ReactDOM.createPortal(modalContent, document.body);
    }
}

// PropTypes validation
PrinterSettingsModal.propTypes = {
    onClose: require("prop-types").func.isRequired,
};

export default PrinterSettingsModal;
